import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setEditorSelectedId } from '../../Reducers/Configurator/Actions';
import { getEditorSelectedId } from '../../Reducers/Configurator/Selectors';

/** Deselect when clicking outside the canvas and sidebar (e.g. header). Canvas empty clicks are handled separately. */
export function useDeselectEditorOnClickAway() {
  const dispatch = useDispatch();
  const selectedId = useSelector(getEditorSelectedId);

  useEffect(() => {
    if (!selectedId) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('[data-editor-canvas], [data-editor-sidebar]')) return;
      dispatch(setEditorSelectedId(null));
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [selectedId, dispatch]);
}

/**
 * Marks a portaled overlay's paper as "part of the editor sidebar".
 *
 * MUI overlays opened from inside the editor (Select/Menu, Popover, Tooltip, Dialog, …)
 * render in a portal on <body>, *outside* the `[data-editor-sidebar]` DOM. A pointerdown
 * inside them would therefore look like a click-away to the hook above and deselect the
 * focused item — collapsing its open card. Spread this onto the overlay's paper props so
 * clicks within it are treated as inside the sidebar.
 *
 *   <Select MenuProps={{ PaperProps: editorSidebarPaperProps({ className }) }} />
 *   <Popover PaperProps={editorSidebarPaperProps({ className })} />
 */
export function editorSidebarPaperProps<T extends object>(paperProps: T = {} as T) {
  return { ...paperProps, 'data-editor-sidebar': true } as T & { 'data-editor-sidebar': true };
}
